import React, { useContext, useEffect, useState } from "react";
import {
    CardTableCell,
    CardTableCellHeader,
    CardTableEmptyState,
    CardTableRow,
    CardTableRows,
    CardTableShell,
    CardTableToolbar,
    FlexContainer,
    LottieLoadingIndicator,
    PortalSearchInput,
    PortalTableActions,
    PortalTableControls,
    Toast
} from "src/components";
import { USER_GROUP } from "src/constants/perks";
import { ActionButton, TextLabel } from "../../components/style";
import { AUDIENCE_ACTIONS } from "../../constants";
import { EmailCampaignsContext } from "../../EmailCampaignsContext";
import { deleteAudience, getAudiences } from "../../api/audienceApi";
import { ContactsTable } from "./contacts/ViewContactsTable";
import { UploadNewContactsTable } from "./contacts/UploadNewContactsTable";
import DeleteAudience from "../../components/audience/DeleteAudience";
import TableAudienceName from "./TableAudienceName";
import TableAudienceActions from "./TableAudienceActions";
import UpdateAudienceName from "../../components/audience/UpdateAudienceName";
import { UpdateContactsTable } from "./contacts/UpdateContactsTable";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { useTableHooks } from "../../hooks/useTableHooks";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";

const tableHeadings = [
    { id: "name", name: "Audience Name", size: "250px" },
    { id: "contactsCount", name: "Contacts Available", size: "130px" },
    { id: "actions", name: "Actions", size: "170px" },
];

export default function CustomAudienceTable() {
    const { setCreateNewAudience, uploadedCsvData, initialFetchSettings } = useContext(EmailCampaignsContext);
    const [selectedAction, setSelectedAction] = useState({});
    const [audienceIsFetching, setAudienceIsFetching] = useState(false);
    const [audienceList, setAudienceList] = useState([]);
    const [selectedAudience, setSelectedAudience] = useState(null);
    const [isViewingContacts, setIsViewingContacts] = useState(false);

    const [isUpdatingContacts, setIsUpdatingContacts] = useState(false);

    const [showUpdateAudienceName, setShowUpdateAudienceName] = useState(false);

    const [showDeleteAudienceDialog, setShowDeleteAudienceDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAudienceToDelete, setSelectedAudienceToDelete] = useState([]);

    const isCustomAudience = name => {
        return ![USER_GROUP.EMPLOYEES, USER_GROUP.CLIENTS].includes(name);
    };

    const toggleDeleteAudienceDialog = () => {
        setShowDeleteAudienceDialog(!showDeleteAudienceDialog);
    };

    const onChangeAction = (audience, value) => {
        setSelectedAudience(audience);
        setSelectedAction((prev) => ({
            ...prev,
            [audience.uuid]: value,
        }));
        if (
            value === AUDIENCE_ACTIONS.MANAGE_AUDIENCE
        ) {
            setIsUpdatingContacts(true);
        }
        if (value === AUDIENCE_ACTIONS.VIEW_CONTACTS) {
            setIsViewingContacts(true);
        }
        if (value === AUDIENCE_ACTIONS.EDIT_AUDIENCE_NAME) {
            setShowUpdateAudienceName(true);
        }
        if (value === AUDIENCE_ACTIONS.DELETE_AUDIENCE) {
            toggleDeleteAudienceDialog();
        }
    };

    const getAllAudience = async () => {
        setAudienceIsFetching(true);
        try {
            const response = await getAudiences();
            if (response) setAudienceList(response.customAudiences);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error occurred while fetching audiences.");
        } finally {
            setAudienceIsFetching(false);
        }
    };

    useEffect(() => {
        getAllAudience();
    }, [uploadedCsvData]);

    const onDeleteAudience = async () => {
        setIsLoading(true);
        try {
            const response = await deleteAudience(selectedAudience.uuid);
            if (response) Toast.success("Audience has been successfully deleted.");
            setIsLoading(false);
            getAllAudience();
            toggleDeleteAudienceDialog();
            setSelectedAction({});
            setSelectedAudience(null);
            initialFetchSettings();
        } catch (error) {
            console.log("Error: ", error);
            Toast.error(error.getApiErrorDetails().message);
            setIsLoading(false);
        }
    };

    const audienceIsSelected = (uuid) =>
        selectedAudienceToDelete.some(u => u.uuid === uuid);

    const onSelectAudienceToDelete = (audience) => {
        setSelectedAudienceToDelete(prev => {
            const isAlreadySelected = prev.some(q => q.uuid === audience.uuid);
            if (isAlreadySelected) {
                return prev.filter(q => q.uuid !== audience.uuid);
            } else {
                return [...prev, audience];
            }
        });
    };

    const handleDeleteSelectedAudience = async () => {
        const selectedUUIDs = selectedAudienceToDelete.map(email => email.uuid);

        setIsLoading(true);
        let successCount = 0;
        try {
            for (const uuid of selectedUUIDs) {
                const response = await deleteAudience(uuid);

                if (response.info === "success") {
                    successCount++;
                }
            }
            if (successCount > 0) {
                Toast.success("Selected audience deleted successfully.");
                initialFetchSettings();
            }
        } catch (error) {
            console.error("Error deleting selected audience: ", error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setSelectedAudienceToDelete([]);
            setIsLoading(false);
            getAllAudience();
        }
    };

    const { tableData, onSearchItem } = useTableHooks(audienceList, {});

    if (isViewingContacts)
        return (
            <ContactsTable
                selectedAudience={selectedAudience}
                setIsViewingContacts={setIsViewingContacts}
                setSelectedAction={setSelectedAction}
                isViewingContacts={true}
            />
        );

    if (isUpdatingContacts)
        return (
            <UpdateContactsTable
                selectedAudience={selectedAudience}
                setIsUpdatingContacts={setIsUpdatingContacts}
                setSelectedAction={setSelectedAction}
            />
        );

    if (uploadedCsvData.length > 0) {
        return <UploadNewContactsTable />;
    }

    return (
        <>
            <CardTableShell minHeight="48rem">
                {audienceIsFetching ? (
                    <FlexContainer
                        justifyContent={audienceIsFetching && "center"}
                        alignItems="center"
                        color="#fff"
                        style={{
                            overflowX: "auto",
                            maxWidth: "98.625rem",
                            minHeight: "100%",
                        }}
                    >
                        <LottieLoadingIndicator />
                    </FlexContainer>
                ) : (
                    <>
                        <CardTableToolbar>
                            <FlexContainer maxWidth="700px">
                                <TextLabel fontWeight="bold" fontSize="20px">
                                    Manage Custom Audiences & Email Lists
                                </TextLabel>
                                <TextLabel fontSize="15px" color="#612684">
                                    Create and manage your custom audience lists for email campaigns. Add
                                    users individually or upload a CSV file to build segmented lists for
                                    more targeted communication.
                                </TextLabel>
                            </FlexContainer>
                        </CardTableToolbar>
                        <CardTableToolbar>
                            <PortalTableControls>
                                <PortalSearchInput
                                    width="min(100%, 487px)"
                                    placeholder="Search Audience"
                                    type="text"
                                    onChange={(e) => onSearchItem(e)}
                                />
                                {selectedAudienceToDelete.length > 0 ?
                                    isLoading ? (
                                        <LoadingIndicatorButton width="84px" height="39px">
                                            <LottieLoadingIndicator
                                                height="2rem"
                                                width="2rem"
                                                margin="5px"
                                                containerHeight="2rem"
                                            />
                                        </LoadingIndicatorButton>
                                    ) : (
                                        <ActionButton
                                            width="140px"
                                            height="39px"
                                            border="1.5px solid #FF3B3B"
                                            color="#FF3B3B"
                                            backgroundColor="#FFF5F5"
                                            onClick={() => handleDeleteSelectedAudience()}
                                        >
                                            Delete Audience
                                        </ActionButton>)
                                    : null}
                            </PortalTableControls>
                            <PortalTableActions>
                                <ActionButton
                                    width="184px"
                                    height="39px"
                                    color="#FFFFFF"
                                    backgroundColor="#171A22"
                                    border="2px solid #171A22"
                                    onClick={() => setCreateNewAudience(true)}
                                >
                                    New Audience
                                </ActionButton>
                            </PortalTableActions>
                        </CardTableToolbar>
                        <CardTableRows maxHeight="none" minHeight="32rem">
                            {tableData.length === 0 && (
                                <CardTableEmptyState>
                                    No custom audiences found.
                                </CardTableEmptyState>
                            )}
                            {audienceList &&
                            tableData.map((audience, index) => (
                                <CardTableRow
                                    key={audience.uuid || index}
                                    selected={audienceIsSelected(audience.uuid)}
                                    columns="2.25rem minmax(15rem, 1.6fr) minmax(8rem, 0.75fr) minmax(11rem, 0.9fr)"
                                    minWidth="760px"
                                    padding="1rem 0.85rem"
                                >
                                    <CardTableCell alignItems="center">
                                        <CardTableCellHeader>
                                            { isCustomAudience(audience.name) ?
                                                <Checkbox
                                                    checked={audienceIsSelected(audience.uuid)}
                                                    onClickCallback={() => onSelectAudienceToDelete(audience)}
                                                    radius="3px"
                                                    border="1px solid #DBE5ED"
                                                    color="#FFFFFF"
                                                    position="0% 0%"
                                                /> :
                                                <div style={{
                                                    height: "17px",
                                                    width: "17px"
                                                }} />
                                            }
                                        </CardTableCellHeader>
                                    </CardTableCell>
                                    <CardTableCell>
                                        <CardTableCellHeader>
                                            {tableHeadings[0].name}
                                        </CardTableCellHeader>
                                        <TableAudienceName
                                            audience={audience}
                                            setIsUpdatingContacts={setIsUpdatingContacts}
                                            setSelectedAudience={setSelectedAudience}
                                        />
                                    </CardTableCell>
                                    <CardTableCell>
                                        <CardTableCellHeader>
                                            {tableHeadings[1].name}
                                        </CardTableCellHeader>
                                        <TextLabel>
                                            {audience.contactsCount}
                                        </TextLabel>
                                    </CardTableCell>
                                    <CardTableCell>
                                        <CardTableCellHeader>
                                            {tableHeadings[2].name}
                                        </CardTableCellHeader>
                                        <TableAudienceActions
                                            selectedAction={selectedAction}
                                            audience={audience}
                                            onChangeAction={onChangeAction}
                                        />
                                    </CardTableCell>
                                </CardTableRow>
                            ))}
                        </CardTableRows>
                    </>
                )}
            </CardTableShell>
            {showUpdateAudienceName && (
                <UpdateAudienceName
                    setShowUpdateAudienceName={setShowUpdateAudienceName}
                    selectedAudience={selectedAudience}
                    getAllAudience={getAllAudience}
                    setSelectedAction={setSelectedAction}
                />
            )}
            {showDeleteAudienceDialog && (
                <DeleteAudience
                    title={"Are you sure you want to delete this audience?"}
                    subtitle="This action cannot be undone."
                    toggleModal={toggleDeleteAudienceDialog}
                    deleteFunction={onDeleteAudience}
                    isLoading={isLoading}
                    setSelectedAction={setSelectedAction}
                />
            )}
        </>
    );
}
